import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ContextService } from '@maximizer/core/shared/data-access';
import {
  EntityType,
  EntryType,
  Octopus,
  TimelineEvent,
  TimelineHistoricDetailsEvent,
  TimelineHistoricPagination,
} from '@maximizer/core/shared/domain';
import { map, Observable } from 'rxjs';
import { TimelineFilter, TimelineKeys } from '../state/timeline.models';
import { TimelineService } from './timeline.service';

@Injectable()
export class TimelineHistoricService extends TimelineService {
  readonly fieldsHistoricDetails: Octopus.Scope<Octopus.EntryHistoricDetails> =
    {
      Key: 1,
    };

  readonly fieldsHistoric: Octopus.Scope<Octopus.TimelineHistoric> = {
    '$LEFT(Description, 200)': 1,
    '$LEFT(Subject, 200)': 1,
    AbEntries: [
      {
        Key: 1,
        DisplayValue: 1,
      },
    ],
    Attachment: 1,
    Completed: {
      Value: 1,
      DisplayValue: 1,
    },
    Creator: {
      Value: 1,
      DisplayValue: 1,
    },
    Direction: 1,
    DocumentType: {
      Value: 1,
      DisplayValue: 1,
    },
    FileNames: 1,
    Key: 1,
    Leads: [
      {
        Key: 1,
        DisplayValue: 1,
      },
    ],
    NoteType: {
      Value: 1,
      DisplayValue: 1,
    },
    Category: {
      Value: 1,
      DisplayValue: 1,
    },
    Recurring: 1,
    SecStatus: {
      CanRead: 1,
      CanDelete: 1,
      CanUpdate: 1,
    },
    Source: 1,
    StartDate: {
      Value: 1,
      DisplayValue: 1,
    },
    Type: {
      Icon: 1,
      Key: 1,
      DisplayValue: 1,
      External: 1,
    },
    Users: [
      {
        Key: {
          Uid: 1,
          Value: 1,
        },
        DisplayValue: 1,
      },
    ],
    With: {
      Opportunity: {
        Key: 1,
        Objective: 1,
      },
      Case: {
        Key: 1,
        CaseNumber: 1,
        Subject: 1,
      },
    },
    WithKey: 1,
    WorkflowInstanceKey: 1,
  };

  constructor(http: HttpClient, context: ContextService) {
    super(http, context);
  }

  forEntryDetails(
    key: string,
    type: EntityType,
  ): Observable<TimelineHistoricDetailsEvent | null> {
    const criteria = {
      SearchQuery: {
        Key: {
          $EQ: key,
        },
      },
    };
    let request:
      | Octopus.TimelineHistoricDetailsReadRequest
      | Octopus.AppointmentHistoricDetailsReadRequest
      | Octopus.NoteHistoricDetailsReadRequest;

    if (type === 'appointment') {
      request = {
        Appointment: {
          Scope: {
            Fields: {
              ...this.fieldsHistoricDetails,
              Subject: 1,
              Description: 1,
              DescriptionRichText: 1,
            },
          },
          Criteria: criteria,
        },
      };
    } else if (type === 'note') {
      request = {
        Note: {
          Scope: {
            Fields: {
              ...this.fieldsHistoricDetails,
              Text: 1,
              RichText: 1,
            },
          },
          Criteria: criteria,
        },
      };
    } else {
      request = {
        Timeline: {
          Scope: {
            Fields: {
              ...this.fieldsHistoricDetails,
              Subject: 1,
              Description: 1,
            },
          },
          Criteria: criteria,
        },
      };
    }

    request.Globalization = Octopus.DefaultGlobalization;
    request.Compatibility = { TaskObject: '2.0' };

    return this.http
      .post<
        | Octopus.TimelineHistoricDetailsResponse
        | Octopus.AppointmentHistoricDetailsResponse
        | Octopus.NoteHistoricDetailsResponse
      >(`${this.context.api}${Octopus.Action.READ}`, request)
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const mapper = new Octopus.TimelineHistoricMapper();

            if (type === 'appointment') {
              return mapper.fromDetails(
                (result as Octopus.AppointmentHistoricDetailsResponse)
                  .Appointment.Data,
              );
            } else if (type === 'note') {
              return mapper.fromDetails(
                (result as Octopus.NoteHistoricDetailsResponse).Note.Data,
              );
            }

            return mapper.fromDetails(
              (result as Octopus.TimelineHistoricDetailsResponse).Timeline.Data,
            );
          }
          return null;
        }),
      );
  }

  forEntryWithPaginationKey(
    key: string | TimelineKeys,
    type: EntryType,
    filter: TimelineFilter,
    nextPageKey?: string,
    pageSize = 50,
  ): Observable<TimelineHistoricPagination> {
    const commonFilter = {
      ...filter,
      customDateQuery: {
        $LE: '$TODAY()',
      },
    };
    const criteria = this.getCriteriaForKeys<Octopus.TimelineHistoric>(
      key,
      type,
      commonFilter,
    );

    const request: Octopus.TimelineHistoricReadRequest = {
      Timeline: {
        Scope: { Fields: this.fieldsHistoric },
        Criteria: criteria,
        OrderBy: {
          Fields: [
            {
              StartDate: 'DESC',
            },
          ],
          PageKey: nextPageKey,
          PageSize: pageSize,
        },
      },
      Globalization: Octopus.DefaultGlobalization,
      Compatibility: { TaskObject: '2.0' },
    };

    return this.http
      .post<Octopus.TimelineHistoricResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(map((result) => this.processResponse(result)));
  }

  getLeadAndAbEntryKeyFrom(opportunityKey: string): Observable<TimelineKeys> {
    const request: Octopus.LeadReadRequest & Octopus.OpportunityReadRequest = {
      Lead: {
        Criteria: {
          SearchQuery: {
            OpportunityKey: {
              $EQ: opportunityKey,
            },
          },
        },
        Scope: {
          Fields: {
            Key: 1,
          },
        },
      },
      Opportunity: {
        Criteria: {
          SearchQuery: {
            Key: {
              $EQ: opportunityKey,
            },
          },
        },
        Scope: {
          Fields: {
            AbEntry: {
              Key: 1,
            },
            Contact: {
              Key: 1,
            },
          },
        },
      },
    };
    request.Globalization = Octopus.DefaultGlobalization;

    return this.http
      .post<
        Octopus.LeadResponse & Octopus.OpportunityResponse
      >(`${this.context.api}${Octopus.Action.READ}`, request)
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const abEntryKey =
              result.Opportunity?.Data?.[0]?.['AbEntry']?.Key ?? undefined;
            const contactKey =
              result.Opportunity?.Data?.[0]?.['Contact']?.Key ?? undefined;
            return {
              leadKey: result.Lead?.Data?.[0]?.Key ?? undefined,
              abEntryKey: abEntryKey ?? contactKey,
              opportunityKey,
            };
          }

          return {
            leadKey: undefined,
            abEntryKey: undefined,
            opportunityKey,
          };
        }),
      );
  }

  getAbEntryOrContactKeyFrom(caseKey: string): Observable<TimelineKeys> {
    const request: Octopus.CaseReadRequest = {
      Case: {
        Criteria: {
          SearchQuery: {
            Key: {
              $EQ: caseKey,
            },
          },
        },
        Scope: {
          Fields: {
            AbEntryKey: 1,
            ContactKey: 1,
          },
        },
      },
    };
    request.Globalization = Octopus.DefaultGlobalization;

    return this.http
      .post<Octopus.CaseResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const abEntryKey =
              (result.Case?.Data?.[0]?.['AbEntryKey'] as string) ?? undefined;
            const contactKey =
              (result.Case?.Data?.[0]?.['ContactKey'] as string) ?? undefined;
            return {
              caseKey,
              abEntryKey: abEntryKey || contactKey,
            };
          }

          return {
            caseKey,
            abEntryKey: undefined,
          };
        }),
      );
  }

  getEntryByKey(key: string): Observable<TimelineHistoricPagination> {
    const request: Octopus.TimelineHistoricReadRequest = {
      Timeline: {
        Scope: { Fields: this.fieldsHistoric },
        Criteria: {
          SearchQuery: {
            Key: {
              $EQ: key,
            },
          },
        },
      },
      Globalization: Octopus.DefaultGlobalization,
      Compatibility: { TaskObject: '2.0' },
    };

    return this.http
      .post<Octopus.TimelineHistoricResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(map((result) => this.processResponse(result)));
  }

  override getFilterQuery({
    interactions,
    notes,
    categories,
    showAllCategories,
  }: TimelineFilter): Octopus.LogicalQuery<Octopus.Timeline> {
    const query: Octopus.LogicalQuery<Octopus.Timeline> = {
      $OR: [],
    };

    const hasCategories = !!categories?.length && !showAllCategories;

    if (hasCategories) {
      const includeDocuments =
        interactions.length === 0 ||
        interactions.includes(Octopus.DefaultInteractions.DOCUMENT);
      const includeAllNotes =
        (interactions.length === 0 && notes.length === 0) ||
        interactions.includes(Octopus.DefaultInteractions.NOTE);
      const categoryFilter: Octopus.LogicalQuery<Octopus.Timeline> = {
        $AND: [
          {
            Category: {
              $IN: categories,
            },
          },
        ],
      };

      if (interactions?.length) {
        categoryFilter.$AND?.push({
          Type: {
            $IN: interactions,
          },
        });
      }

      query.$OR?.push(categoryFilter);

      if (includeDocuments) {
        query.$OR?.push({
          Type: {
            $EQ: Octopus.DefaultInteractions.DOCUMENT,
          },
        });
      }

      if (includeAllNotes) {
        query.$OR?.push({
          Type: {
            $EQ: Octopus.DefaultInteractions.NOTE,
          },
        });
      }
    } else if (interactions?.length) {
      query.$OR?.push({
        Type: {
          $IN: interactions,
        },
      });
    }

    if (notes.length) {
      query.$OR?.push({
        NoteType: {
          $IN: notes,
        },
      });
    }

    return query;
  }

  private processResponse(response: Octopus.TimelineHistoricResponse): {
    data: TimelineEvent[];
    nextPageKey?: string;
  } {
    const result: {
      data: TimelineEvent[];
      nextPageKey?: string;
    } = {
      data: [],
    };

    if (
      response.Code === Octopus.ResponseStatusCode.Successful &&
      response.Timeline?.Data
    ) {
      const mapper = new Octopus.TimelineHistoricMapper();
      result.data = mapper.from(response.Timeline.Data);
      result.nextPageKey = response.Timeline.NextPageKey;
    }
    return result;
  }
}
